{
  "actions": {
    "addCredential": "Add credential",
    "addNewCredential": "Add New Credential",
    "backToRecipes": "Back to recipes",
    "backToSignIn": "$t(common:passwordLogin.forgotPassword.backToSignIn)",
    "bookACall": "Book a call →",
    "clearHistory": "Clear history",
    "createSandboxAccount": "Create sandbox account",
    "exploreSandbox": "Explore Sandbox",
    "forgotPassword": "Forgot password?",
    "generateCredentials": "Generate credentials",
    "hideResponse": "Hide response",
    "iveCopiedMySecret": "I've copied my secret",
    "newCredential": "New credential",
    "openSandbox": "Open sandbox",
    "readDocs": "Read docs →",
    "replay": "Replay",
    "requestNewLink": "Request a new link",
    "resetPassword": "$t(common:passwordLogin.forgotPassword.resetPassword)",
    "runAgain": "Run again",
    "runRecipe": "Run recipe",
    "runRecipeCta": "Run recipe →",
    "send": "Send",
    "sendResetLink": "Send Reset Link →",
    "sending": "Sending",
    "sendingLink": "Sending link…",
    "signIn": "$t(common:passwordLogin.signIn)",
    "signingIn": "Signing in…",
    "saveNewPassword": "Save new password",
    "updatePassword": "Update password",
    "updating": "Updating…",
    "viewResponse": "View response"
  },
  "auth": {
    "alreadyHaveAccount": "Already have an account?",
    "createWorkspace": "Creating workspace…",
    "fineprint": "Sandbox data is isolated from production. © Thredd 2026",
    "newToThredd": "New to Thredd?",
    "subtitle": {
      "signIn": "Sign into the sandbox to continue exploring the Thredd APIs.",
      "signUp": "Issue cards, move money, and simulate transactions — all isolated from production."
    },
    "title": {
      "signIn": "Welcome to the Thredd Sandbox",
      "signUp": "Create a Sandbox Account"
    }
  },
  "components": {
    "authScreen": {
      "agreeToTerms": "I agree to the <termsLink>Sandbox Terms of Use</termsLink>",
      "createAccountLink": "Create a sandbox account",
      "marketingConsent": "I consent to receiving marketing emails from Thredd UK Limited about its products, services, promotions and events (optional)"
    },
    "codeSamples": {
      "copySnippet": "Copy snippet"
    },
    "credentialCard": {
      "clientSecret": "Client secret",
      "confirmDelete": {
        "confirmation": "Are you sure you want to continue?",
        "description": "This action cannot be undone. This sandbox OAuth Credential will be permanently deleted.",
        "title": "Delete \"{{name}}\" OAuth credential?"
      },
      "copyClientId": "Copy client ID",
      "copySecret": "Copy secret",
      "deleteCredential": "Delete credential",
      "meta": "Created {{date}} · Last used {{lastUsed}}",
      "regenerateSecretKey": "Regenerate Secret Key",
      "rotateSecret": "Rotate secret"
    },
    "credentials": {
      "empty": "Add new OAuth Credentials to see them here.",
      "message": {
        "credentialDeleted": "\"{{name}}\" credential deleted.",
        "credentialDeleteFailed": "Failed to delete \"{{name}}\" credential.",
        "secretRotated": "Secret rotated"
      }
    },
    "downloadCollectionModal": {
      "title": "Download Postman Collection",
      "description": "Get started quickly with ready-to-use API requests in Postman.",
      "credentialLabel": "OAuth Credential",
      "download": "Download Collection"
    },
    "endpointDocs": {
      "tableHeaders": {
        "status": "Error Code",
        "label": "Error label",
        "description": "Error Description"
      },
      "errorCodes": {
        "400": "Invalid JSON, malformed payload, or missing required field.",
        "401": "Bearer token missing, expired, or revoked.",
        "403": "Token does not have a scope required for this endpoint.",
        "404": "Resource does not exist or is not visible to your tenant.",
        "422": "Payload parsed but failed business-rule validation.",
        "429": "Sandbox rate limit exceeded — back off and retry with exponential delay.",
        "500": "Unexpected error. Retry idempotent calls; contact support if it persists."
      },
      "labels": {
        "badRequest": "Bad Request",
        "errorCodes": "Error Codes",
        "forbidden": "Forbidden",
        "internalServerError": "Internal Server Error",
        "method": "$t(common:labels.method)",
        "notFound": "Not Found",
        "operationId": "Operation ID",
        "overview": "OVERVIEW",
        "pathParameters": "PATH PARAMETERS",
        "queryParameters": "QUERY PARAMETERS",
        "requestBody": "REQUEST BODY",
        "required": "required",
        "responseShape": "RESPONSE SHAPE",
        "tooManyRequests": "Too Many Requests",
        "unauthorized": "Unauthorized",
        "validationError": "$t(common:errors.validation)"
      }
    },
    "endpointList": {
      "noEndpointsMatch": "No endpoints match \"{{query}}\"."
    },
    "verifyEmailScreen": {
      "title": "Check your inbox",
      "description": "We've sent a verification email to {{email}}.",
      "instruction": "Open the verification email and use the link to activate your account."
    },
    "verifyEmailConfirmScreen": {
      "verifying": "Verifying your email...",
      "successTitle": "Email verified!",
      "successDescription": "Your account is now active. Redirecting to sign in...",
      "alreadyVerifiedTitle": "Already verified",
      "alreadyVerifiedDescription": "Your email was already verified. Redirecting to sign in...",
      "errorTitle": "Verification failed",
      "errorDescription": "This verification link is invalid or has expired.",
      "missingTokenTitle": "Missing verification token",
      "missingTokenDescription": "The verification link you used is incomplete. Please check your email for the full link or contact support.",
      "contactUs": "Contact us"
    },
    "forgotPasswordScreen": {
      "checkEmail": {
        "description": "We've sent a password reset link to {{email}}. The link expires in 30 minutes.",
        "title": "Check your email"
      },
      "form": {
        "description": "Enter the email address associated with your account, and we'll send you a password reset link.",
        "title": "Request Password Reset"
      }
    },
    "header": {
      "brandPill": "SANDBOX",
      "envLabel": "api.sandbox.thredd.com"
    },
    "history": {
      "breadcrumb": "Sandbox / Request history",
      "description": "Every request you've sent in this sandbox session. Replay, copy, or inspect.",
      "empty": {
        "body": "Go to the Sandbox and send a request — it'll show up here.",
        "title": "No requests yet"
      },
      "title": "Request history"
    },
    "newCredentialModal": {
      "createCredential": "Create OAuth Credential",
      "credentialName": "OAuth Credential Name",
      "labelPlaceholder": "e.g. Local dev, Mobile tests",
      "title": "Add New OAuth Credential"
    },
    "recipeRunner": {
      "completeCount": "{{count}} of {{total}} steps complete",
      "notFound": {
        "title": "Recipe not found"
      },
      "recipe": "RECIPE",
      "running": "Running…",
      "recipeComplete": {
        "title": "Recipe complete",
        "body": "All {{count}} requests finished successfully. Review the responses above."
      }
    },
    "recipesIndex": {
      "breadcrumb": "Sandbox / Recipes",
      "description": "Guided, multi-step API sequences for common workflows. Click any to run step by step.",
      "stepsLabel": "{{count}} steps"
    },
    "recentRequests": {
      "empty": "Requests you send will appear here.",
      "hide": "Hide Recent Requests",
      "noResults": "No requests match your search.",
      "show": "Show Recent Requests",
      "title": "Recent Requests"
    },
    "requestPane": {
      "authDescription": "Bearer token derived from the active sandbox OAuth Credential. Manage OAuth Credentials from the OAuth section.",
      "authenticated": "Authenticated",
      "unauthenticated": "Unauthenticated",
      "emptyResponse": "Press Send to call the endpoint. Sandbox calls are mocked and return realistic example responses.",
      "copyHeader": "Copy Authorization header",
      "copyToken": "Copy token",
      "headersTable": {
        "authorization": "Bearer <your_api_key>",
        "contentType": "application/json"
      },
      "noCredential": "Select an OAuth Credential from the dropdown below to enable the Send button.",
      "noCredentials": {
        "title": "No OAuth Credentials Found",
        "description": "You haven't created any OAuth Credentials yet. To view your authorisation settings, create your credentials in the <credentialsLink>OAuth Credentials</credentialsLink> tab."
      },
      "noPathParams": "No path parameters.",
      "noQueryParams": "No query parameters for this endpoint.",
      "response": "Response",
      "selectCredentialPlaceholder": "Select an OAuth Credential",
      "sendingRequest": "Sending request"
    },
    "revealSecretModal": {
      "copyTitle": "Click the Copy icon to copy the Secret Key",
      "copyWarning": "This is the only time you'll be able to view this secret. Copy it now and store it securely. If you lose it, you'll need to generate a new one.",
      "copyAndClose": "Copy and Close",
      "secretKey": "Secret Key",
      "title": {
        "rotate": "Secret Key Regenerated"
      },
      "tokenExpiryInfo": "Tokens expire after 1 hour, or when the browser tab is closed. You will need to generate a new Secret Key each time you use these credentials."
    },
    "sandboxHeader": {
      "changePassword": "Change password",
      "collapseMenu": "Collapse menu",
      "expandMenu": "Expand menu",
      "logout": "Logout",
      "postmanCollection": "Postman Collection",
      "sandboxHelp": "Sandbox Help",
      "switchToDarkMode": "Switch to dark mode",
      "switchToLightMode": "Switch to light mode"
    },
    "sandboxHelpModal": {
      "tabs": {
        "whatIsSandbox": "What is Sandbox?",
        "generateCredentials": "Generate Credentials",
        "postman": "Postman"
      },
      "perfectFor": "Perfect for:",
      "whatIsSandbox": {
        "intro": "Explore and evaluate Thredd's APIs in an environment designed to help you understand our platform, build integrations, and familiarise yourself with key workflows."
      },
      "generateCredentials": {
        "description": "Before accessing any API endpoints, you'll need to create API credentials.",
        "instructions": "Use the OAuth Credentials page to create a Client ID and Client Secret to use the API Explorer."
      },
      "apiExplorer": {
        "title": "Using the API Explorer",
        "description": "Use the built-in API Explorer to make requests directly from the sandbox.",
        "bullets": {
          "quickTesting": "Quickly testing endpoints",
          "exploringBehaviour": "Exploring API behaviour",
          "learningFormats": "Learning request and response formats"
        }
      },
      "postman": {
        "title": "Using Postman",
        "description": "Alternatively you can download our Postman collection and interact with the APIs using your preferred API client.",
        "bullets": {
          "buildingPrototypes": "Building integration prototypes",
          "savingRequests": "Saving requests and environments",
          "testingWorkflows": "Testing workflows across multiple endpoints"
        }
      }
    },
    "updatePasswordModal": {
      "title": "Reset your Password?",
      "subtitle": "Create a new password",
      "repeatPasswordTooltip": "Must match the password entered above.",
      "savePasswordSuccess": "New password created."
    },
    "sandboxSidebar": {
      "labels": {
        "sandbox": "API Explorer",
        "recipes": "Recipes"
      },
      "promo": {
        "body": "Talk to us about moving your sandbox app to a live programme.",
        "label": "Ready for prod?"
      }
    },
    "resetPasswordScreen": {
      "expired": {
        "title": "This link has expired",
        "description": "For your security, password reset links expire after 30 minutes. This link has expired. To continue, request a new password reset.",
        "sendResetLink": "Send reset link →"
      },
      "success": {
        "description": "Your password has been reset. Redirecting to sign in…",
        "message": "Password reset — sign in with your new password",
        "title": "Password updated"
      },
      "chooseNewPassword": "Choose a new password",
      "greeting": "Hi {{name}},",
      "greetingFallback": "Hi there,",
      "formSubtitle": "Create a strong, unique password to secure your sandbox account.",
      "savePasswordAndLaunch": "Save Password and Launch Sandbox →",
      "withEmail": "Resetting password for {{email}}.",
      "withoutEmail": "Pick a strong password you don't use anywhere else."
    },
    "explorer": {
      "loadingEndpoints": "Loading endpoints…",
      "noEndpointsAvailable": "No endpoints available."
    },
    "comingSoon": {
      "title": "Coming Soon",
      "subTitle": "The Thredd Sandbox is not yet available. Check back later."
    },
    "feedbackWidget": {
      "sentiment": {
        "question": "Overall, how satisfied are you with the Thredd Sandbox?"
      },
      "rating": {
        "easeOfGettingStarted": "Ease of getting started"
      },
      "comment": {
        "placeholder": "Tell us about anything that could make your Sandbox experience better."
      }
    },
    "termsOfUse": {
      "title": "Sandbox Terms of Use Policy",
      "intro": "Please read these terms and conditions carefully before using this site.",
      "sections": {
        "whoWeAre": {
          "heading": "Who we are and how to contact us",
          "siteUrl": "https://sandbox.thredd.ai/",
          "body": "(\"Our Site\" or \"Sandbox\") is a site operated by Thredd (UK) Limited (\"We\" or \"Us\" or \"Thredd\"). We are a private limited company. We are registered in England and Wales under company number 09926803 and have our registered office at 2nd Floor 33 Kingsway, London, United Kingdom, WC2B 6UF. To contact us, please email sandbox@thredd.com."
        },
        "otherTerms": {
          "heading": "There are other terms that may apply to you",
          "intro": "These terms of use refer to the following additional terms, which also apply to your use of our site (together \"Terms\"):",
          "items": {
            "privacyPolicy": "Our <privacyPolicyLink>Privacy Policy</privacyPolicyLink>.",
            "termsOfUsePolicy": "Our Terms of Use Policy (\"This Policy\" or \"Sandbox's Terms of Use Policy\"), which sets out the permitted uses and prohibited uses of our Sandbox's testing environment.",
            "cookiePolicy": "Our <cookiePolicyLink>Cookie Policy</cookiePolicyLink>, which sets out information about the cookies on our site.",
            "websiteTerms": "Our <websiteTermsLink>Website Terms and Conditions</websiteTermsLink>."
          }
        },
        "changesToTerms": {
          "heading": "We may make changes to these terms",
          "body": "We amend these terms from time to time to reflect changes to our products, our users' needs and our business priorities. Every time you wish to use our site, please check these terms to ensure you understand the terms that apply at that time."
        },
        "materialUse": {
          "heading": "How you may use material on our site",
          "paragraphs": {
            "intellectualProperty": "We are the owner or the licensee of all intellectual property rights in our site, and in the material published on it. Those works are protected by copyright laws and treaties around the world. All such rights are reserved.",
            "personalUseCopy": "You may print off one copy, and may download extracts, of any page(s) from our site for your personal use and you may draw the attention of others within your organisation to content posted on our site.",
            "attributionRequired": "Our status (and that of any identified contributors) as the authors of content on our site must always be acknowledged (except where the content is user-generated)."
          }
        },
        "prohibitedUses": {
          "heading": "Prohibited uses",
          "intro": "You must not use our site:",
          "items": {
            "unlawfulRegulationBreach": "In any way that breaches any applicable local, national or international law or regulation.",
            "fraudulentPurpose": "In any way that is unlawful or fraudulent or has any unlawful or fraudulent purpose or effect or whether knowingly or unknowingly anticipating, assisting, or facilitating any forms of criminal or unethical activities.",
            "malwareTransmission": "To knowingly transmit any data, send or upload any material that contains viruses, Trojan horses, worms, time-bombs, keystroke loggers, spyware, adware or any other harmful programs or similar computer code designed to adversely affect the operation of any computer software or hardware."
          },
          "agreeIntro": "You also agree must:",
          "agreeItems": {
            "noReproduction": "Not to reproduce, duplicate, copy, modify or re-sell any part of our site or codes of our testing environment, digital or physical copies of any materials you have printed off or downloaded from our site in any way, and in contravention of the provisions of our Terms.",
            "noCommercialUseWithoutLicence": "Not use any part of the content or codes of our Sandbox's testing environment on our site for commercial purposes without obtaining a licence to do so from us or our licensors.",
            "noUnauthorisedAccessIntro": "Not to access without authority, interfere with, damage or disrupt:"
          },
          "accessItems": {
            "site": "any part of our site;",
            "hostingEquipment": "any equipment or network on which our site is stored;",
            "hostingSoftware": "any software used in the provision of our site; or",
            "thirdPartyEquipment": "any equipment or network or software owned or used by any third party."
          }
        },
        "contentStandards": {
          "heading": "Content standards",
          "intro": "These content standards apply to any and all material which you contribute to our site (\"Contribution\"), and to any interactive services associated with a subset of our REST APIs associated with it.",
          "paragraphs": {
            "spiritAndLetter": "The Content Standards must be complied with in spirit as well as to the letter. The standards apply to each part of any Contribution as well as to its whole.",
            "threddDiscretion": "THREDD will determine, in its discretion, whether a Contribution breaches the Content Standards."
          },
          "mustIntro": "A Contribution must:",
          "mustItems": {
            "artificialData": "Be artificial data (where all uploaded information should not be able to identifiable as a true individual regardless of such individual is known or unknown to you)",
            "lawfulUseOnly": "Comply with the law applicable in England and Wales and in any country from which it is posted."
          },
          "mustNotIntro": "A Contribution must not:",
          "mustNotItems": {
            "noPersonalData": "Be any identifiable personal data, including but not limited to name, address, date of birth, company name, active card number, expire date and so on.",
            "noDatabaseRightInfringement": "Infringe any database right of any other person.",
            "noThirdPartyDutyBreach": "Breach any legal duty owed to a third party, such as a contractual duty or a duty of confidence.",
            "noImpersonation": "Impersonate any person or affiliation with any person.",
            "noFalseThreddAssociation": "Give the impression that the Contribution emanates from THREDD, if this is not the case.",
            "noMaliciousCode": "Advocate, promote, incite any party to commit, or assist any unlawful or criminal act such as (by way of example only) copyright infringement or misuse any identifiable card information or identifiable personal information."
          }
        },
        "breachOfPolicy": {
          "heading": "Breach of this policy",
          "paragraphs": {
            "breachDetermination": "When we consider that a breach of this Sandbox's Terms of Use Policy has occurred, we may take such action as we deem appropriate.",
            "immediateRightCessation": "If you print off, copy, download, attempted to modify, share or repost any part of our site in breach of these terms of use, your right to use our site will cease immediately and you must, at our option, immediately return or destroy any copies of the materials in any forms you have made."
          },
          "actionsIntro": "Failure to comply with the Terms constitutes a material breach of the terms of use upon which you are permitted to use our site, and may result in our taking all or any of the following actions:",
          "actionsItems": {
            "withdrawalOfAccess": "Immediate, temporary or permanent withdrawal of your right to use our site.",
            "removalOfContribution": "Immediate, temporary or permanent removal of any Contribution uploaded by you to our site.",
            "warning": "Issue of a warning to you.",
            "legalProceedings": "Legal proceedings against you for reimbursement of all costs on an indemnity basis (including, but not limited to, reasonable administrative and legal costs) resulting from the breach.",
            "furtherLegalAction": "Further legal action against you.",
            "disclosureToAuthorities": "Disclosure of such information to law enforcement authorities as we reasonably feel is necessary or as required by law."
          },
          "liabilityExclusion": "We exclude our liability for all action we may take in response to breaches of this acceptable use policy. The actions we may take are not limited to those described above, and we may take any other action we reasonably deem appropriate."
        },
        "suspendSite": {
          "heading": "We may suspend or withdraw our site",
          "paragraphs": {
            "freeOfCharge": "Our site is made available free of charge. We do not guarantee that our site, or any content on it, will always be available or be uninterrupted. We may suspend or withdraw or restrict the availability of all or any part of our site for business or operational reasons. We will endeavour to give you reasonable notice of any suspension or withdrawal where possible.",
            "userResponsibility": "You are also responsible for ensuring that all persons who access our site through your internet connection are aware of these terms of use and other applicable terms and conditions, and that they comply with them."
          }
        },
        "accountSafety": {
          "heading": "You must keep your account details safe",
          "paragraphs": {
            "confidentialCredentials": "If you choose, or you are provided with, a user identification code, password or any other piece of information as part of our security procedures, you must treat such information as confidential. You must not disclose it to any third party.",
            "disableCredentials": "We have the right to disable any user identification code or password, whether chosen by you or allocated by us, at any time, if in our opinion you have failed to comply with any of the provisions of these terms of use.",
            "notifyBreach": "If you know or suspect that anyone other than you know your user identification code or password or any unauthorised use of your user identification code or password or fraudulent attempted log-in of which it becomes aware, you must promptly notify us at <infosecLink>infosec@thredd.com</infosecLink> and <sandboxLink>sandbox@thredd.com</sandboxLink>. You will notify us as soon as possible, and in any event within such time limits as may be required by any applicable regulations or laws, of any breach of security relating to the \"Sandbox\" or THREDD of which it becomes aware to the extent that it may be reasonably likely to affect THREDD."
          }
        },
        "noReliance": {
          "heading": "Do not rely on information on this site",
          "paragraphs": {
            "generalInformationOnly": "The content on our site is provided for our Sandbox's testing environment general information only. It is not intended to amount to advice on which you should rely in any other environments, other than our sandbox's testing environment. You must obtain professional or specialist advice before taking, or refraining from, any action on the basis of the content on our site.",
            "noAccuracyGuarantee": "Although we make reasonable efforts to update the information on our site, we make no representations, warranties or guarantees, whether express or implied, that the content is accurate, complete or up to date."
          }
        },
        "liability": {
          "heading": "Our responsibility for loss or damage suffered by you",
          "intro": "Whether you are an individual or a business user:",
          "items": {
            "domesticUseOnly": "Please note that we only provide our Sandbox's testing environment for domestic and private use. You agree not to use our site for any commercial or business purposes.",
            "noLiabilityExclusionForDeathOrInjury": "We do not exclude or limit in any way our liability where it would be unlawful to do so. This includes liability for death or personal injury caused by our negligence or the negligence of our employees, agents or subcontractors and for fraud or fraudulent misrepresentation.",
            "noImpliedWarranties": "We exclude all implied conditions, warranties, representations or other terms that may apply to our site or content on it.",
            "noLiabilityForLossOrDamage": "We will not be liable to you for any loss or damage, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, even if foreseeable, arising under or in connection with use of, or inability to use, our site; or use of or reliance on any content displayed on our site.",
            "noLiabilityForConsequentialLoss": "We have no liability to you for any loss of profit, loss of business, business interruption, loss of anticipated savings or loss of business opportunity, goodwill or reputation or any indirect or consequential loss or damage."
          }
        },
        "personalInformation": {
          "heading": "How we may use your personal information",
          "body": "We will only use your personal information as set out in our <termsLink>Terms</termsLink>."
        },
        "uploadedContent": {
          "heading": "Rights you are giving us to use material you upload",
          "intro": "When you upload or post content to our site, you grant us the following rights to use that content:",
          "items": {
            "contentLicenceGrant": "A worldwide, non-exclusive, royalty-free, transferable licence to use, reproduce, distribute, prepare derivative works of, display, and perform that user-generated content in connection with the service provided by the website and across different media including to promote the site or the service until such time as the user deletes the content from the site;"
          }
        },
        "viruses": {
          "heading": "We are not responsible for viruses and you must not introduce them",
          "paragraphs": {
            "noSecurityGuarantee": "We do not guarantee that our site will be secure or free from bugs or viruses.",
            "userDeviceResponsibility": "You are responsible for configuring your information technology, computer programmes and platform to access our site. You should use your own virus protection software.",
            "noMaliciousAttacks": "You must not misuse our site by knowingly introducing viruses, trojans, worms, logic bombs or other material that is malicious or technologically harmful. You must not attempt to gain unauthorised access to our site, the server on which our site is stored or any server, computer or database connected to our site. You must not attack our site via a denial-of-service attack or a distributed denial-of-service attack. By breaching this provision, you would commit a criminal offence under the Computer Misuse Act 1990. We will report any such breach to the relevant law enforcement authorities and we will co-operate with those authorities by disclosing your identity to them. In the event of such a breach, your right to use our site will cease immediately."
          }
        },
        "governingLaw": {
          "heading": "Which country's laws apply to any disputes?",
          "body": "These terms of use, their subject matter and their formation (and any non-contractual disputes or claims) are governed by English law. We both irrevocably agree to the exclusive jurisdiction of the courts of England and Wales."
        }
      }
    }
  },
  "labels": {
    "credentials": "OAuth Credentials",
    "clientId": "CLIENT ID",
    "clientSecret": "CLIENT SECRET",
    "company": "Company",
    "confirmPassword": "Confirm password",
    "repeatPassword": "Repeat password",
    "confirmNewPassword": "$t(common:passwordLogin.confirmPasswordLabel)",
    "currentPassword": "Current password",
    "auth": "Auth",
    "body": "Body",
    "docs": "Docs",
    "example": "Example",
    "fullName": "Full name",
    "headers": "Headers",
    "parameters": "Params",
    "label": "Label",
    "newPassword": "$t(common:passwordLogin.newPasswordLabel)",
    "password": "$t(common:passwordLogin.passwordLabel)",
    "pretty": "Pretty",
    "queued": "Queued",
    "raw": "Raw",
    "workEmail": "Work email",
    "poweredBy": "Powered by",
    "explorerHomepageDescription": "Use this isolated environment to build, test, and debug your card programs. Select a resource to explore available endpoints, configure test payloads, and make live API calls against the sandbox.",
    "parameter": "Parameter",
    "authorization": "Authorization",
    "contentType": "Content-Type"
  },
  "placeholders": {
    "analyticalEngines": "Analytical Engines Ltd",
    "firstName": "Ada",
    "lastName": "Lovelace",
    "fullName": "Ada Lovelace",
    "password": "••••••••",
    "role": "Your job role",
    "country": "United States",
    "workEmail": "ada.lovelace@name.com"
  },
  "tooltips": {
    "confirmPassword": "Re-enter your password to confirm it matches.",
    "password": "Use the password you set when creating your sandbox account."
  },
  "validation": {
    "confirmNewPassword": "Confirm your new password",
    "emailType": "Enter a valid email address",
    "minChars": "Use at least {{count}} characters",
    "mustIncludeNumber": "Include at least one number",
    "mustIncludeUppercase": "Include at least one uppercase letter",
    "mustAgreeToTerms": "You must agree to the Sandbox Terms of Use to continue",
    "mustIncludeLowercase": "Include at least one lowercase letter",
    "passwordExtra": "Minimum 8 characters, at least one number, one uppercase and one lowercase letter.",
    "passwordsDontMatch": "Passwords don't match",
    "required": "Enter your {{field}}",
    "scopeRequired": "Select at least one scope"
  }
}
